import React, { useCallback, useEffect, useRef, useState } from "react";
import { Play } from "lucide-react";
import clip1Video from "../../assets/Clip1.mp4";
import clip2Video from "../../assets/Clip2.mp4";
import clip3Video from "../../assets/Clip3.mp4";
import clip4Video from "../../assets/Clip4.mp4";
import clip5Video from "../../assets/Clip5.mp4";
import clip6Video from "../../assets/Clip6.mp4";
import clip1Poster from "../../assets/proof-posters/clip-1.webp";
import clip2Poster from "../../assets/proof-posters/clip-2.webp";
import clip3Poster from "../../assets/proof-posters/clip-3.webp";
import clip4Poster from "../../assets/proof-posters/clip-4.webp";
import clip5Poster from "../../assets/proof-posters/clip-5.webp";
import clip6Poster from "../../assets/proof-posters/clip-6.webp";
import { VideoClip } from "./VideoClip";

const exampleClips = [
  { id: 1, videoSrc: clip1Video, title: "TopClip example 1", posterSrc: clip1Poster, views: "2.3M" },
  { id: 2, videoSrc: clip2Video, title: "TopClip example 2", posterSrc: clip2Poster, views: "1.8M" },
  { id: 3, videoSrc: clip3Video, title: "TopClip example 3", posterSrc: clip3Poster, views: "890K" },
  { id: 4, videoSrc: clip4Video, title: "TopClip example 4", posterSrc: clip4Poster, views: "1.2M" },
  { id: 5, videoSrc: clip5Video, title: "TopClip example 5", posterSrc: clip5Poster, views: "650K" },
  { id: 6, videoSrc: clip6Video, title: "TopClip example 6", posterSrc: clip6Poster, views: "3.1M" },
] as const;

export const VideoShowcase: React.FC = () => {
  const [documentIsVisible, setDocumentIsVisible] = useState(
    typeof document === "undefined" || document.visibilityState === "visible"
  );
  const [showPlaybackFallback, setShowPlaybackFallback] = useState(false);
  const [playbackRequest, setPlaybackRequest] = useState(0);
  const playingClipIds = useRef(new Set<number>());

  useEffect(() => {
    const onVisibilityChange = () =>
      setDocumentIsVisible(document.visibilityState === "visible");
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", onVisibilityChange);
  }, []);

  const handlePlaybackBlocked = useCallback((clipId: number) => {
    playingClipIds.current.delete(clipId);
    setShowPlaybackFallback(true);
  }, []);

  const handlePlaybackStarted = useCallback((clipId: number) => {
    playingClipIds.current.add(clipId);
    if (playingClipIds.current.size === exampleClips.length) {
      setShowPlaybackFallback(false);
    }
  }, []);

  const retryAllPlayback = () => {
    setPlaybackRequest((request) => request + 1);
  };

  return (
    <section id="examples" className="bg-white pb-16 pt-4 sm:pb-24" aria-label="TopClip output examples">
      <div className="relative w-full">
        <div aria-hidden="true" className="pointer-events-none absolute inset-y-0 left-0 z-10 w-12 bg-gradient-to-r from-white to-transparent sm:w-24" />
        <div aria-hidden="true" className="pointer-events-none absolute inset-y-0 right-0 z-10 w-12 bg-gradient-to-l from-white to-transparent sm:w-24" />
        {showPlaybackFallback && (
          <div className="pointer-events-none absolute inset-x-0 top-4 z-20 flex justify-center px-4">
            <button
              type="button"
              data-proof-play-fallback
              aria-controls="proof-video-carousel"
              onClick={retryAllPlayback}
              className="pointer-events-auto inline-flex min-h-12 items-center gap-2 rounded-full border border-white/70 bg-[var(--tc-blue)] px-5 py-2 text-base font-bold text-white shadow-[0_12px_34px_rgba(13,98,255,0.30)] transition hover:-translate-y-0.5 hover:bg-blue-700"
            >
              <Play aria-hidden="true" className="h-5 w-5 fill-current" />
              Play clips
            </button>
          </div>
        )}
        <div
          id="proof-video-carousel"
          data-proof-scroller
          tabIndex={0}
          role="region"
          aria-label="Scroll through TopClip example clips"
          className="scrollbar-hide overflow-x-auto overscroll-x-contain pb-8"
        >
          <div className="mx-auto flex w-max snap-x snap-mandatory gap-4 px-4 sm:gap-6 sm:px-8">
            {exampleClips.map((clip) => (
              <VideoClip
                key={clip.id}
                clip={clip}
                documentIsVisible={documentIsVisible}
                playbackRequest={playbackRequest}
                onPlaybackBlocked={handlePlaybackBlocked}
                onPlaybackStarted={handlePlaybackStarted}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
